import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Chart,
  Point,
  Annotation,
  Axis,
  Coordinate,
  registerShape,
} from 'bizcharts';
import { commaSeperator } from 'utils';

registerShape('point', 'pointer', {
  draw(cfg, container) {
    const group = container.addGroup();
    const center = this.parsePoint({ x: 0, y: 0 });
    const start = this.parsePoint({ x: 0, y: 0.5 });

    // خط عقربه گیج
    group.addShape('polygon', {
      attrs: {
        points: [
          [center.x - 3, center.y + 3],
          [center.x + 3, center.y - 3],
          [cfg.x, cfg.y],
        ],
        fill: '#303030',
      },
    });

    // دایره بزرگ
    group.addShape('circle', {
      attrs: {
        x: center.x - 2,
        y: center.y - 2,
        r: 4,
        stroke: '#303030',
        lineWidth: 3,
        fill: '#fff',
      },
    });

    // دایره کوچک داخلی
    group.addShape('circle', {
      attrs: {
        x: center.x - 2,
        y: center.y - 2,
        r: 3,
        stroke: '#fff',
        lineWidth: 2,
        fill: '#303030',
      },
    });

    const preAngle = 0;

    const angle1 = Math.atan((start.y - center.y) / (start.x - center.x));
    const angle = (Math.PI - 2 * angle1) * cfg.points[0].x;

    if (group.cfg.animable) {
      group.animate((ratio) => {
        group.resetMatrix();
        group.rotateAtPoint(
          center.x,
          center.y,
          preAngle + (angle - preAngle) * ratio
        );
      }, 1000);
    } else {
      group.rotateAtPoint(center.x, center.y, angle);
    }
    this.preAngle = angle;

    return group;
  },
});

const GaugeFingerChart = (props) => {
  const {
    min = 0,
    max = 1000,
    value,
    tickInterval = 500,
    height = 272,
    platform,
  } = props;

  const [data, setData] = useState([]);
  const [scale, setScale] = useState({
    value: {
      min, //کمترین عدد گیج
      max, //بیشترین عدد گیج
      tickInterval, //تقسیمات روی گیج
      formatter: (v) => commaSeperator(v * 1),
    },
  });

  let finalMax = 101.875;
  if (platform.includes('telegram')) {
    finalMax = 2356.25;
  } else if (platform.includes('twitter')) {
    finalMax = 1457.05;
  } else if (platform.includes('instagram')) {
    finalMax = 601;
  }

  // سورت کردن بر اساس تعداد
  useEffect(() => {
    if (value) {
      setScale({
        value: {
          ...scale.value,
          max: finalMax,
          tickInterval: finalMax / 2,
        },
      });

      setData([
        {
          value: (value > finalMax ? finalMax : value) / 2,
          origin: value > finalMax ? finalMax : value,
        },
      ]);
    } else {
      setData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Chart
      padding={[0, 0, 10, 0]}
      height={height}
      data={data}
      scale={scale}
      autoFit
    >
      {/* بازه گیج به درجه */}
      <Coordinate
        type='polar'
        radius={0.8}
        startAngle={(-10 / 10) * Math.PI}
        endAngle={(0 / 10) * Math.PI}
      />
      <Axis name='1' visible={false} />

      {/* اعداد روی کیج */}
      <Axis visible={false} />

      {/* عقربه */}
      <Point
        position='value*1'
        color='#303030'
        shape='pointer'
        pin={{
          style: {
            fontSize: 11,
            textAlign: 'center',
            textBaseline: 'middle',
          },
        }}
      />

      {/* قسمت غیر فعال طوسی رنگ */}
      <Annotation.Arc
        start={[0, 1]}
        end={[scale.value.max, 1]}
        style={{
          stroke: '#919EAB29',
          lineWidth: 10,
          lineDash: null,
          lineCap: 'round',
        }}
      />

      {/* قسمت فعال */}
      <Annotation.Arc
        start={[0, 1]}
        end={[value > finalMax ? finalMax : value, 1]}
        style={{
          stroke: 'l(45) 0:#5BE584 1:#00AB55',
          lineWidth: 12,
          lineDash: null,
          lineCap: 'round',
        }}
        top={false}
      />

      {/* عدد زیر گیج */}
      <Annotation.Text visible={false} />

      {/* متن زیر عدد - مطلب در ساعت */}
      <Annotation.Text visible={false} />
    </Chart>
  );
};

GaugeFingerChart.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  value: PropTypes.number,
  height: PropTypes.number,
  platform: PropTypes.string,
  tickInterval: PropTypes.number,
};

export default memo(GaugeFingerChart);
